import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { invoiceService } from '../../services/invoiceService';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, invoice: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!token) {
        setIsLoading(false);
        setError('Authentication required.');
        return;
      }
      try {
        setIsLoading(true);
        const response = await fetch('/api/invoices', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.message || 'Failed to fetch invoices');
        }
        const data = await response.json();
        if (data.status === 'success') {
          setInvoices(data.data.invoices || []);
        } else {
          throw new Error(data.message || 'Could not parse invoices data');
        }
      } catch (err) {
        console.error('Error fetching invoices:', err);
        setError(err.message || 'Failed to load invoices.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [token]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleDeleteClick = (invoice) => {
    setDeleteModal({ isOpen: true, invoice });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.invoice) return;
    
    setIsDeleting(true);
    try {
      await invoiceService.deleteInvoice(deleteModal.invoice.id);
      setInvoices(prev => prev.filter(inv => inv.id !== deleteModal.invoice.id));
      setDeleteModal({ isOpen: false, invoice: null });
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.message || 'Failed to delete invoice');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, invoice: null });
  };

  // Check if user has permission to delete
  const canDelete = user && ['admin', 'manager'].includes(user.role);

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-brand-text-dark">Invoice Management</h1>
          <p className="text-lg text-gray-600 mt-2">
            View, create, and manage customer invoices.
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/invoices/new')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-150 ease-in-out"
        >
          + Create New Invoice
        </button>
      </header>

      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <p className="text-xl text-gray-500">Loading invoices...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {!isLoading && !error && (
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            {invoices.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-500 text-lg">No invoices found.</p>
                <p className="text-gray-400 mt-2">Why not create one now?</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800">
                        <Link to={`/admin/invoices/view/${invoice.id}`}>{invoice.invoiceNumber}</Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{invoice.client?.username || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.invoiceDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.dueDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">${parseFloat(invoice.totalAmount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${invoice.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' : invoice.status === 'paid' ? 'bg-green-100 text-green-800' : invoice.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                          {invoice.status ? invoice.status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                        <Link to={`/admin/invoices/view/${invoice.id}`} className="text-indigo-600 hover:text-indigo-900 mr-3">View</Link>
                        <Link to={`/admin/invoices/edit/${invoice.id}`} className="text-yellow-600 hover:text-yellow-900 mr-3">Edit</Link>
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteClick(invoice)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        itemType="Invoice"
        itemName={deleteModal.invoice?.invoiceNumber}
      />
    </div>
  );
};

export default InvoicesPage;